import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { publicBaseUrl } from "../common/envconst";
import { useCartUserTracking } from "../common/hooks/useCartUserTracking";
import { useLinkPayCookies } from "../common/hooks/useLinkPayCookies";
import { useCookie } from "../common/hooks/useCookie";
import { useCartActions } from "../contexts/CartServiceContext/CartServiceContext";
import { useConfigActions, useConfigContext } from "../contexts/ConfigContext/ConfigContext";
import { isPaymentMethodEnabled, PAYMENT_METHOD } from "../contexts/ConfigContext/paymentMethods";
import { APP_MODE, IStallMode } from "../contexts/ConfigContext/types";
import { setDatadogGlobalContext } from "../services/datadog";
import { usePageViewTracking } from "./hooks/usePageViewTracking";

const FAIRPRICE_LOCATION_NAME = "Punggol Digital District";

type INavigateWithParams = (path: string, params?: Record<string, string>) => void;
export const useNavigateWithParams = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const navigateWithParams: INavigateWithParams = useCallback(
		(path, params) => {
			// Preserve existing uuid parameter
			const currentParams = new URLSearchParams(location.search);
			const uuid = currentParams.get("uuid");

			const searchParams = new URLSearchParams(params || {});

			// If current URL has uuid, preserve it
			if (uuid) {
				searchParams.set("uuid", uuid);
			}

			if (searchParams.toString()) {
				navigate(`${path}?${searchParams.toString()}`);
			} else {
				navigate(path);
			}
		},
		[location, navigate]
	);

	return { navigateWithParams };
};

interface IUrlParams {
	[key: string]: string;
}

export const useUrlParams = (): IUrlParams => {
	const location = useLocation();

	const params: IUrlParams = useMemo(() => {
		const queryParams = new URLSearchParams(location.search);
		const paramsObject: IUrlParams = {};

		for (const [key, value] of queryParams.entries()) {
			paramsObject[key] = value;
		}

		return paramsObject;
	}, [location.search]);

	return params;
};

export const getUUIDFromUrl = (): string | null => {
	if (typeof globalThis === "undefined" || !globalThis.location) {
		return null;
	}

	const urlParams = new URLSearchParams(globalThis.location.search);

	return urlParams.get("uuid");
};

export const getTableFromUrl = (): string | null => {
	if (typeof globalThis === "undefined" || !globalThis.location) {
		return null;
	}

	const urlParams = new URLSearchParams(globalThis.location.search);

	return urlParams.get("table");
};

export const getQrsIdFromUrl = (): string | null => {
	if (typeof globalThis === "undefined" || !globalThis.location) {
		return null;
	}

	const urlParams = new URLSearchParams(globalThis.location.search);

	return urlParams.get("qrs_id");
};

export const useAppInit = () => {
	const configActions = useConfigActions();
	const cartActions = useCartActions();
	const urlUuid = getUUIDFromUrl();
	const urlTable = getTableFromUrl();
	const urlQrsId = getQrsIdFromUrl();
	const { state: stallMode } = useConfigContext((state) => state.config?.stall.mode);
	const { state: inited } = useConfigContext((state) => state.inited);
	const { state: configLocationId } = useConfigContext((state) => state.location.id);
	const { state: paymentMethods } = useConfigContext((state) => state.paymentMethods);
	const { state: linkpayV3Enabled } = useConfigContext(
		(state) => state.config?.linkpay_v3_enabled
	);
	// Passive: the gate reacts through its effect deps; it must never arm the
	// cookie hook's whole-app reload on venues that do not even run the v2 flow.
	const { isFullySetup } = useLinkPayCookies({ passive: true });

	const { cookieValue: storedLocationId, setCookie: setLocationCookie } = useCookie(
		"location_id",
		"",
		{
			expires: 0.5,
			secure: true,
			sameSite: "strict" as const,
			path: publicBaseUrl,
		}
	);
	const { cookieValue: storedTable, setCookie: setTableCookie } = useCookie("table", "", {
		expires: 0.5,
		secure: true,
		sameSite: "strict" as const,
		path: publicBaseUrl,
	});
	const { cookieValue: storedQrsId, setCookie: setQrsIdCookie } = useCookie("qrs_id", "", {
		expires: 0.5,
		secure: true,
		sameSite: "strict" as const,
		path: publicBaseUrl,
	});

	// Track user UID changes and manage cart accordingly
	// Handles user state transitions:
	// - Guest -> User (login): inherit guest cart
	// - User -> Guest (logout): clear cart
	// - User A -> User B (switch): clear cart
	useCartUserTracking();

	usePageViewTracking();

	useEffect(() => {
		if (inited) {
			setDatadogGlobalContext("brand", stallMode);
			setDatadogGlobalContext("bbid", configLocationId);
		}
	}, [inited, stallMode, configLocationId]);

	const locationId = useMemo(() => {
		if (urlUuid) {
			setLocationCookie(urlUuid);

			return urlUuid;
		}

		// 2. Get from cookie
		return storedLocationId ?? null;
	}, [urlUuid, storedLocationId, setLocationCookie]);

	const table = useMemo(() => {
		if (urlTable) {
			setTableCookie(urlTable);

			return urlTable;
		}

		// 2. Get from cookie
		return storedTable ?? null;
	}, [urlTable, storedTable, setTableCookie]);

	const qrsId = useMemo(() => {
		if (urlQrsId) {
			setQrsIdCookie(urlQrsId);

			return urlQrsId;
		}

		// 2. Get from cookie
		return storedQrsId ?? null;
	}, [urlQrsId, storedQrsId, setQrsIdCookie]);

	useEffect(() => {
		if (locationId) {
			configActions.INIT_LOCATION_ID_AND_TABLE({ location_id: locationId, table });
			cartActions.CART_INIT();
		}
	}, [locationId, configActions, cartActions, stallMode, table]);

	/**
	 * One rule for every stall: a venue with a USABLE way to pay takes orders; one with
	 * none is browse-only. Adyen on is usable by anyone; LinkPay on is usable by anyone
	 * under v3 (signing in happens on the payment page), but under v2 only the injected
	 * cookies can pay — without the full set that venue has nothing to offer.
	 */
	useEffect(() => {
		if (stallMode === IStallMode.FAIRPRICE) {
			document.title = "The Grocer Bar";
		} else if (stallMode === IStallMode.FLINT) {
			document.title = "Flint Order ";
		} else if (stallMode === IStallMode.WANG) {
			document.title = "Heavenly Wang";
		} else {
			document.title = "Self Order";
		}

		const linkPayUsable =
			isPaymentMethodEnabled(paymentMethods, PAYMENT_METHOD.LINKPAY) &&
			((linkpayV3Enabled ?? false) || isFullySetup);
		const canPay = linkPayUsable || isPaymentMethodEnabled(paymentMethods, PAYMENT_METHOD.ADYEN);

		configActions.SET_APP_MODE(canPay ? APP_MODE.NORMAL : APP_MODE.VIEW);
	}, [stallMode, configActions, paymentMethods, linkpayV3Enabled, isFullySetup]);

	return { qrsId, locationId };
};
